/**
 * Mobile Navbar Component
 *
 * Mobile navigation bar with full-screen expandable menu overlay.
 * Features: Full-screen overlay, multi-level taxonomy navigation, search bar
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";

import { Bookmark, MessagesSquare, ShoppingCart, User } from "lucide-react";
import { v4 as uuidv4 } from "uuid";

import { WideSearchBar } from "@/components/shared/WideSearchBar";
import { SUPPORT_EMAIL_COSIMO } from "@/constants/support-emails";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useChat } from "@/contexts/ChatContext";
import { useSearchContext } from "@/contexts/SearchContext";
import { useSignUpPrompt } from "@/contexts/SignUpPromptContext";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useNavigationMenu } from "@/hooks/useNavigationMenu";
import { OctogenSpinner } from "@octogen/schema-analyzer/loading";
import type { BrowseMenuRootSummary } from "@/lib/browse-menu-roots";
import type { BrowseMenuNode } from "@/lib/browse-menu-tree";
import { buildFeaturedSearchChatData, getFeaturedEditorialItem } from "@/lib/editorial-config";
import { browseByMenu, navigateToAgentResults } from "@/lib/results-navigation";
import {
  BRANDS_PATH,
  BRAND_EXPLORER_PATH,
  CART_PATH,
  COLLECTIONS_PATH,
  PROFILE_PATH,
} from "@/lib/route-paths";
import { colors } from "@/styles/design-tokens";
import { SECTION_PADDING_X } from "@/styles/responsive-spacing";
import {
  displayGenderToCategoryToken,
  mapGenderToCatalog,
  mapGenderToDisplay,
} from "@/utils/genderMapping";

import { BookmarkIcon } from "./icons/BookmarkIcon";
import { CartIcon } from "./icons/CartIcon";
import { HamburgerIcon } from "./icons/HamburgerIcon";
import { SearchIcon } from "./icons/SearchIcon";
import { XIcon } from "./icons/XIcon";
import { LogoContainer } from "./LogoContainer";
import { GenderLink } from "./navigation/GenderLink";

// Shared style classes
const linkClass = "text-action text-ink";
const footerLinkClass = "text-action-muted text-ink-secondary";

// Reusable components
function ExpandableChevron({ isExpanded, size = 14 }: { isExpanded: boolean; size?: number }) {
  return (
    <Image
      src="/icons/chevron.svg"
      alt=""
      width={size}
      height={size}
      style={{
        transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
        transition: "transform 0.2s",
      }}
    />
  );
}

function LinkButton({
  label,
  onClick,
  textClass = linkClass,
  className = "py-2",
}: {
  label: string;
  onClick: () => void;
  textClass?: string;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center transition-opacity hover:opacity-70 ${className}`}
    >
      <span className={textClass}>{label}</span>
    </button>
  );
}

function MenuItem({
  label,
  onClick,
  hasChevron,
  icon,
  count,
}: {
  label: string;
  onClick: () => void;
  hasChevron?: boolean;
  icon?: React.ReactNode;
  count?: number;
}) {
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center justify-between py-4 transition-opacity hover:opacity-70"
    >
      <div className="flex items-center gap-3">
        {icon}
        <span className="text-body-lg text-ink">{label}</span>
        {count !== undefined && count > 0 && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-black text-xs text-white">
            {count}
          </span>
        )}
      </div>
      {hasChevron && <Image src="/icons/chevron.svg" alt="" width={14} height={14} />}
    </button>
  );
}

function SectionHeader({ label }: { label: string }) {
  return <p className="text-overline-wide text-ink-faint pb-2 pt-4">{label}</p>;
}

function FeaturedCard({
  title,
  image,
  onClick,
}: {
  title: string;
  image?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="mb-4 flex w-full items-center gap-4 text-left transition-opacity hover:opacity-80"
    >
      {image ? (
        <div className="relative h-20 w-24 flex-shrink-0 overflow-hidden rounded bg-gray-100">
          <Image src={image} alt={title} fill style={{ objectFit: "cover" }} />
        </div>
      ) : null}
      <div className="flex flex-col">
        <p className="text-title text-ink" style={{ fontSize: "13px" }}>
          {title}
        </p>
        <div className="mt-1 flex items-center gap-1">
          <span className="text-overline text-ink-faint" style={{ fontSize: "10px" }}>
            SHOP NOW
          </span>
          <Image src="/icons/chevron.svg" alt="" width={10} height={10} />
        </div>
      </div>
    </button>
  );
}

// Props
interface MobileNavbarProps {
  variant?: "landing" | "default";
  browseMenuRoots?: BrowseMenuRootSummary[] | null;
}

function MenuItemLink({
  label,
  href,
  onClick,
  hasChevron = false,
}: {
  label: string;
  href: string;
  onClick?: () => void;
  hasChevron?: boolean;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className="flex w-full items-center justify-between py-4 transition-opacity hover:opacity-70"
    >
      <div className="flex items-center gap-3">
        <span className="text-body-lg text-ink">{label}</span>
      </div>
      {hasChevron && <Image src="/icons/chevron.svg" alt="" width={14} height={14} />}
    </Link>
  );
}

export function MobileNavbar({ variant = "default", browseMenuRoots = null }: MobileNavbarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [expandedLane, setExpandedLane] = useState<string | null>(null);
  const [expandedDepartment, setExpandedDepartment] = useState<string | null>(null);
  const [shouldFocusSearch, setShouldFocusSearch] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { getTotalItems } = useCart();
  const { isAnonymous, userId } = useAuth();
  const { showSignUpPrompt } = useSignUpPrompt();
  const { clearChat, setChatCategory } = useChat();
  const { activeSearchParams, updateSearchParam, recentSearches } = useSearchContext();
  const selectedGender = mapGenderToDisplay(activeSearchParams.gender);
  const filteredRecentSearches = useMemo(
    () => (recentSearches ?? []).filter((item) => item.search_label?.trim()).slice(0, 4),
    [recentSearches]
  );
  const cartCount = getTotalItems();
  const menu = useNavigationMenu(browseMenuRoots);
  const { roots, rootsLoading: isBrowseMenuLoading, rootsError: browseMenuError } = menu;
  const loadMenu = () => {
    void menu.ensureLoaded().catch(() => {});
  };

  // Lock body scroll and notify other components when menu is open.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("mobileMenuToggle", { detail: { isOpen: isExpanded } }));
  }, [isExpanded]);

  useBodyScrollLock({ key: "mobile-navbar", enabled: isExpanded });

  // Auto-focus search input when opening menu via search icon
  useEffect(() => {
    if (isExpanded && shouldFocusSearch && searchInputRef.current) {
      // Small delay to ensure the menu is rendered
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
        setShouldFocusSearch(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, shouldFocusSearch]);

  const closeMenu = useCallback(() => {
    setIsExpanded(false);
    setSearchValue("");
    setSearchFocused(false);
    setExpandedLane(null);
    setExpandedDepartment(null);
    setShouldFocusSearch(false);
  }, []);

  useEffect(() => {
    if (!isExpanded) return;
    const key = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
        requestAnimationFrame(() =>
          document.querySelector<HTMLButtonElement>('[aria-label="Open menu"]')?.focus()
        );
      }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [isExpanded, closeMenu]);

  const toggleLaneExpansion = (laneId: string) => {
    setExpandedLane((prev) => (prev === laneId ? null : laneId));
    setExpandedDepartment(null);
    if (expandedLane !== laneId) loadMenu();
  };

  const toggleDepartmentExpansion = useCallback((deptName: string) => {
    setExpandedDepartment((prev) => (prev === deptName ? null : deptName));
  }, []);

  const handleBrowseMenuClick = useCallback(
    (node: BrowseMenuNode) => {
      const threadId = uuidv4();
      browseByMenu(router, threadId, {
        facets: node.search_query.facets || [],
        display_path: node.display_path,
        navbar_category: node.display_path[0] || node.display_name,
      });
      closeMenu();
    },
    [router, closeMenu]
  );

  const submitSearch = useCallback(
    (query: string) => {
      const userInput = query.trim();
      if (!userInput) return;
      const chatId = uuidv4();

      clearChat();
      const categoryToken = displayGenderToCategoryToken(selectedGender);

      const chatData = {
        query: userInput,
        originalQuery: userInput,
        gender: selectedGender,
        ...(categoryToken ? { category: categoryToken } : {}),
        userId,
        timestamp: Date.now(),
      };
      sessionStorage.setItem(`chat-${chatId}`, JSON.stringify(chatData));
      setChatCategory(chatId, categoryToken);

      navigateToAgentResults(router, chatId);
      closeMenu();
    },
    [selectedGender, userId, clearChat, setChatCategory, router, closeMenu]
  );

  const handleSearchSubmit = useCallback(() => {
    submitSearch(searchValue);
  }, [searchValue, submitSearch]);

  const handleBookmarkClick = useCallback(() => {
    if (isAnonymous) {
      showSignUpPrompt("collection");
      return;
    }
    router.push(COLLECTIONS_PATH);
    closeMenu();
  }, [closeMenu, isAnonymous, router, showSignUpPrompt]);

  const handleCartClick = useCallback(() => {
    if (isAnonymous) {
      showSignUpPrompt("cart");
      return;
    }
    router.push(CART_PATH);
    closeMenu();
  }, [closeMenu, isAnonymous, router, showSignUpPrompt]);

  const handleChatClick = useCallback(() => {
    router.push("/chats");
    closeMenu();
  }, [router, closeMenu]);

  const findRootById = useCallback(
    (rootId: string | null) => menu.index?.roots.find((root) => root.uuid === rootId) || null,
    [menu.index]
  );

  const navigateTo = useCallback(
    (path: string) => {
      router.push(path);
      closeMenu();
    },
    [router, closeMenu]
  );

  // Render department item with optional subcategories
  const renderDepartment = (dept: BrowseMenuNode) => {
    const hasSubcategories = Boolean(dept.children_nodes && dept.children_nodes.length > 0);
    const isDeptExpanded = expandedDepartment === dept.uuid;

    return (
      <div key={dept.uuid}>
        <button
          aria-expanded={hasSubcategories ? isDeptExpanded : undefined}
          aria-controls={hasSubcategories ? `mobile-dept-${dept.uuid}` : undefined}
          onClick={() =>
            hasSubcategories ? toggleDepartmentExpansion(dept.uuid) : handleBrowseMenuClick(dept)
          }
          className="flex w-full items-center justify-between py-2 transition-opacity hover:opacity-70"
        >
          <span className="text-body-lg text-ink">{dept.display_name}</span>
          {hasSubcategories && <ExpandableChevron isExpanded={isDeptExpanded} size={12} />}
        </button>
        {hasSubcategories && isDeptExpanded && dept.children_nodes && (
          <div id={`mobile-dept-${dept.uuid}`} className="pb-1 pl-4">
            {dept.children_nodes.map((sub) => (
              <LinkButton
                key={sub.uuid}
                label={sub.display_name}
                onClick={() => handleBrowseMenuClick(sub)}
                textClass="text-body-lg text-ink-secondary"
                className="py-1.5"
              />
            ))}
            <LinkButton
              label={`All ${dept.display_name}`}
              onClick={() => handleBrowseMenuClick(dept)}
              textClass="text-body-lg text-ink-secondary"
              className="py-1.5"
            />
          </div>
        )}
      </div>
    );
  };

  // Handler for featured item click - triggers agent search
  const handleFeaturedClick = useCallback(
    (lane: string, label: string) => {
      const chatId = uuidv4();
      // Use shared helper for consistent chat data (handles gender context check + category token)
      const chatData = buildFeaturedSearchChatData(lane, label, userId ?? undefined);
      sessionStorage.setItem(`chat-${chatId}`, JSON.stringify(chatData));

      navigateToAgentResults(router, chatId);
      closeMenu();
    },
    [router, closeMenu, userId]
  );

  // Render lane accordion content
  const renderLaneContent = (laneId: string) => {
    const root = findRootById(laneId);
    // The single notice below SHOP survives removal of the old root's row.
    if (!root && menu.status === "ready") return null;
    if (!root)
      return (
        <div aria-live="polite" className="py-3 pl-6">
          {menu.status === "error" ? (
            <>
              <p>Categories could not be loaded.</p>
              <button onClick={loadMenu}>Retry</button>
            </>
          ) : (
            <OctogenSpinner label="Loading category" />
          )}
        </div>
      );
    const departments = root.children_nodes || [];
    const featured = getFeaturedEditorialItem(root.display_name);

    return (
      <div className="pb-2 pl-6">
        {/* Featured item with subtle background */}
        {featured ? (
          <div
            className="-ml-6 mb-1 pl-6 pt-1"
            style={{ backgroundColor: "rgba(243, 244, 246, 0.5)" }}
          >
            <FeaturedCard
              title={featured.label}
              image={featured.image}
              onClick={() => handleFeaturedClick(root.display_name, featured.label)}
            />
          </div>
        ) : (
          <div
            className="-ml-6 mb-1 pl-6 pt-1"
            style={{ backgroundColor: "rgba(243, 244, 246, 0.5)" }}
          >
            <FeaturedCard
              title={`Shop ${root.display_name}`}
              onClick={() => handleBrowseMenuClick(root)}
            />
          </div>
        )}
        {departments.map(renderDepartment)}
        <LinkButton
          label={`All ${root.display_name}`}
          onClick={() => handleBrowseMenuClick(root)}
          textClass="text-body-lg text-ink"
          className="py-2"
        />
        <LinkButton label="Shop by Brand" onClick={() => navigateTo(BRAND_EXPLORER_PATH)} />
      </div>
    );
  };

  // Main menu content
  const renderMainMenu = () => (
    <div className="flex flex-col">
      {/* Search Bar - reuses WideSearchBar for consistent behavior */}
      <div className="px-4 py-4">
        <WideSearchBar
          value={searchValue}
          onChange={setSearchValue}
          onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          onSubmit={handleSearchSubmit}
          isFocused={searchFocused}
          inputRef={searchInputRef}
        />

        {/* Gender tabs + Recent Searches - only when search is focused */}
        {searchFocused && (
          // onMouseDown preventDefault keeps input focused when tapping tabs/suggestions

          <div
            onMouseDown={(e) => e.preventDefault()}
            style={{ animation: "slideDown 0.4s ease-in-out" }}
          >
            {/* Gender links - reuses GenderLink from landing page for touch-friendly sizing */}
            <div className="flex flex-wrap items-center gap-x-[28px] gap-y-3 pb-4 pt-5">
              {(["Women's", "Men's", "Both (M+W)", "Kids"] as const).map((gender) => (
                <GenderLink
                  key={gender}
                  text={gender}
                  isSelected={selectedGender === gender}
                  onClick={() => updateSearchParam("gender", mapGenderToCatalog(gender) || "women")}
                />
              ))}
            </div>

            {filteredRecentSearches.length > 0 && (
              <>
                <p className="mt-3.5 mb-3 font-sans text-[10px] font-normal leading-[15px] tracking-[1.1172px] uppercase text-[#99a1af]">
                  Recent Searches
                </p>
                <div className="flex flex-col">
                  {filteredRecentSearches.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => submitSearch(item.search_label || "")}
                      className="flex items-start gap-3 py-[14px] text-left transition-opacity hover:opacity-70"
                    >
                      <div
                        className="flex-shrink-0"
                        style={{ width: "12px", height: "12px", marginTop: "5.375px" }}
                      >
                        <Image src="/icons/search-small-white.svg" alt="" width={12} height={12} />
                      </div>
                      <span className="font-sans text-sm font-normal leading-[22.75px] tracking-[-0.1504px] text-[#0a0a0a]">
                        {item.search_label}
                      </span>
                    </button>
                  ))}
                </div>
                <div
                  style={{
                    height: "1px",
                    backgroundColor: colors.divider.mediumGray,
                    marginTop: "14px",
                    marginLeft: "-1rem",
                    marginRight: "-1rem",
                  }}
                />
              </>
            )}
          </div>
        )}
      </div>

      {/* Shop Section */}
      <div className="px-4">
        <SectionHeader label="SHOP" />
        {isBrowseMenuLoading ? (
          <div className="space-y-3 py-3">
            {[0, 1, 2, 3].map((idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div className="h-4 w-28 rounded bg-gray-100" />
                <div className="h-3 w-3 rounded-full bg-gray-100" />
              </div>
            ))}
          </div>
        ) : browseMenuError ? (
          <div role="status">
            Categories are temporarily unavailable.{" "}
            <button onClick={menu.retryRoots}>Retry categories</button>
          </div>
        ) : (
          roots.map((lane) => {
            const isLaneExpanded = expandedLane === lane.uuid;
            return (
              <div key={lane.uuid}>
                <button
                  type="button"
                  aria-expanded={isLaneExpanded}
                  aria-controls={`mobile-root-${lane.uuid}`}
                  data-testid="mobile-taxonomy-root"
                  onClick={() => toggleLaneExpansion(lane.uuid)}
                  className="flex w-full items-center justify-between py-3 transition-opacity hover:opacity-70"
                >
                  <span className="text-body-lg text-ink">{lane.display_name}</span>
                  <ExpandableChevron isExpanded={isLaneExpanded} />
                </button>
                {isLaneExpanded && (
                  <div id={`mobile-root-${lane.uuid}`}>{renderLaneContent(lane.uuid)}</div>
                )}
              </div>
            );
          })
        )}
      </div>

      {expandedLane && menu.index && !findRootById(expandedLane) && (
        <div role="status" className="px-4 py-2">
          This category is no longer available. Choose an updated category.
        </div>
      )}
      {/* Divider */}
      <div className="mx-4 border-t border-black/10" />

      {/* Browse by Brand */}
      <div className="px-4 py-2">
        <MenuItemLink label="Browse by Brand" href={BRANDS_PATH} onClick={closeMenu} hasChevron />
        <button
          onClick={() => navigateTo(BRAND_EXPLORER_PATH)}
          className="flex w-full items-center justify-between pb-4 pt-1 text-left transition-opacity hover:opacity-70"
        >
          <span className="text-body-lg text-ink">Brand Discovery</span>
          <Image src="/icons/chevron.svg" alt="" width={14} height={14} />
        </button>
      </div>

      {/* Divider */}
      <div className="mx-4 border-t border-black/10" />

      {/* User Actions */}
      <div className="px-4">
        <MenuItem
          label="Saved Items"
          onClick={handleBookmarkClick}
          icon={<Bookmark className="h-5 w-5" strokeWidth={1.5} />}
        />
        <MenuItem
          label="Cart"
          onClick={handleCartClick}
          icon={<ShoppingCart className="h-5 w-5" strokeWidth={1.5} />}
          count={cartCount}
        />
        <MenuItem
          label="Chat History"
          onClick={handleChatClick}
          icon={<MessagesSquare className="h-5 w-5" strokeWidth={1.5} />}
        />
        {isAnonymous ? (
          <MenuItem
            label="Log In/Sign Up"
            onClick={() => {
              showSignUpPrompt("general");
              closeMenu();
            }}
          />
        ) : (
          <MenuItem
            label="My Account"
            onClick={() => navigateTo(PROFILE_PATH)}
            icon={<User className="h-5 w-5" strokeWidth={1.5} />}
          />
        )}
      </div>

      {/* Footer Links */}
      <div className="px-4 py-4">
        <LinkButton
          label="Help & Support"
          onClick={() => window.open(`mailto:${SUPPORT_EMAIL_COSIMO}`, "_blank")}
          textClass={footerLinkClass}
        />
      </div>
    </div>
  );

  // Expanded state
  if (isExpanded) {
    return (
      <nav
        className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-white"
        style={{ height: "100dvh" }}
      >
        <div
          className="sticky top-0 z-10 flex w-full items-center justify-between bg-white px-4"
          style={{
            borderBottom: `1px solid ${colors.divider.light}`,
            paddingBlock: "16px",
            minHeight: "56px",
          }}
        >
          <LogoContainer className="flex items-center gap-[7px]" onClick={closeMenu} />
          <XIcon size="large" onClick={closeMenu} />
        </div>
        <div className="flex-1">{renderMainMenu()}</div>
      </nav>
    );
  }

  // Collapsed state
  return (
    <nav className="w-full py-2" style={{ backgroundColor: colors.background.white }}>
      <div
        className="relative flex h-10 w-full items-center justify-between"
        style={{
          paddingLeft: SECTION_PADDING_X.RESPONSIVE,
          paddingRight: SECTION_PADDING_X.RESPONSIVE,
        }}
      >
        <div className="flex h-8 items-center gap-6">
          <HamburgerIcon onClick={() => setIsExpanded(true)} />
          {variant === "default" && (
            <SearchIcon
              size="default"
              onClick={() => {
                setIsExpanded(true);
                setShouldFocusSearch(true);
              }}
            />
          )}
        </div>
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <LogoContainer />
        </div>
        <div className="flex h-8 items-center gap-3 max-[420px]:gap-2">
          <BookmarkIcon size="mobile" onClick={handleBookmarkClick} />
          <CartIcon count={cartCount} size="mobile" onClick={handleCartClick} />
        </div>
      </div>
    </nav>
  );
}
